import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild, SimpleChanges, OnChanges} from '@angular/core';
import {VRGroupService} from '../../vrgroup.service';
import { BaseTrain, CargoInfo, CargoManifest, CargoRow, RunningTrain, ShipmentInfo, WagonRow } from '../../common/train';
import {Subject} from 'rxjs';
import {isEqual, round, uniqBy, uniqWith} from 'lodash-es';
import {BaseComponent} from '../../../../components/general/base/base.component';
import {OverlayPanelComponent} from '../../../../components/overlay-panel/overlay-panel.component';
import {SidebarService} from '../../../../shared/sidebar/sidebar.service';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import {TranslateService} from '@ngx-translate/core';
import {QmDateFormatPipe} from '../../../../components/qm-pipes/qm-date-format.pipe';
import {StateService} from '../../../../shared/state/state.service';
import { LoadingConfig } from '../../../../components/qm-directives/loading.directive';
import { _ } from '~shared/utils';

@Component({
  selector: 'qm-vrgroup-cargo-manifest',
  templateUrl: './cargo-manifest.component.html',
  styleUrls: ['./cargo-manifest.component.scss']
})
export class CargoManifestComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  public train: RunningTrain;
  /**
   * Result overlay reference
   */
  @ViewChild('detailsOverlay')
  private detailsOverlay: OverlayPanelComponent;
  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<boolean> = new Subject();
  // FIXME: these should be in English instead and translation done with the i18n system
  private readonly wagonTableHeadersTranslationKeys = [
    'Littera',
    'Numero',
    'Maakoodi',
    'Lähtöasema',
    'Määräasema',
    'Kokonaispaino',
    'Sisältö',
    'Rahtikirjanumero',
    'Lähettäjä',
    'Vastaanottaja',
    'Vaaratunnus',
    'VAK-koodi',
    'VAK-lipukkeet',
    'VAK-kuvaus',
    'Pyydetty purkupäivä'
  ];
  public manifests: CargoManifest[] = [];
  public groupedManifests: Map<string, CargoRow[]> = new Map();
  public wagons: WagonRow[];
  public wagonTable: any[];

  public wagonTableHeaders: string[] = [];
  public selectedShipmentRow: ShipmentInfo;
  public selectedWagonRow: WagonRow;
  public hoverType: 'RECIPIENT' | 'SENDER' | 'WAGON' | 'SHIP';
  public query: string;
  public details: any;
  public legs: any[] = [];
  public loadingConfig: LoadingConfig = {
    messages: {
      empty: _('Cargo manifest is not available'),
    }
  };

  constructor(private vrGroupService: VRGroupService, private sidebarService: SidebarService, private changeDetector: ChangeDetectorRef,
              private translate: TranslateService, private stateService: StateService) {
    super();
    this.name = 'CargoManifestComponent';
  }

  ngOnInit() {
    console.log('CargoManifest initialized');
    this.generateWagonTableHeaders();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.train.currentValue?._id !== changes.train.previousValue?._id) {
      this.updateTrain();
    }
  }

  /**
   * Angular destructor hook
   */
  ngOnDestroy(): void {
    console.log('CargoManifest destroyed');
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private async updateTrain() {
    this.loading = await this.vrGroupService.getMegafilesForTrain(this.train._id);
    if (!this.loading.isOk) {
      return;
    }
    const megafile = this.loading.data.data;
    console.log(megafile);
    this.manifests = [];
    for (const row of megafile) {
      if (!row || !row.C || !row.C.D || row.C.D.length === 0) {
        continue;
      }
      this.manifests = row.C.D.filter(manifestRow => {
        if (Object.keys(manifestRow).length > 0) {
          return manifestRow.H && manifestRow.H.length > 0;
        }
      });
    }
    console.log(this.manifests);
    this.wagons = [];
    for (const manifest of this.manifests) {
      let maxWeight = 0;
      let maxLength = 0;
      let wagonCount = 0;
      let wagonAxleCount = 0;
      for (const wagon of manifest.H) {
        wagon.I = this.getShipmentInfoForWagon(wagon);
        this.wagons.push(wagon);
        maxWeight += Math.ceil(this.calculateWagonWeight(wagon));
        maxLength += Math.ceil(parseFloat(wagon.KAL_PITUUS));
        wagonAxleCount += Math.ceil(parseFloat(wagon.KAL_AKSELILUKU));
        wagonCount++;
      }
      this.details = {
        JLP_LITTERA: manifest.JLP_LITTERA,
        JLP_LKM: manifest.JLP_LKM,
        JLP_SUURINJUNANOPEUS: manifest.JLP_SUURINJUNANOPEUS,
        maxWeight: maxWeight,
        maxLength: maxLength,
        wagonCount: wagonCount,
        wagonAxleCount: wagonAxleCount,
        JLP_VETURIEN_PITUUS: manifest.JLP_VETURIEN_PITUUS,
        trainLength: maxLength + parseFloat(manifest.JLP_VETURIEN_PITUUS),
        trainAxleCount: wagonAxleCount + parseFloat(manifest.JLP_LKM) * 4
      };
    }
    console.log(this.groupedManifests);

    this.legs = [];
    for (const row of megafile) {
      if (!row || !row.C || !row.C.D || !row.C.JP || row.C.D.length === 0) {
        continue;
      }
      const manifest: any = {};
      console.log(row);
      manifest.JP = Array.isArray(row.C.JP) ? row.C.JP : [row.C.JP];
      this.legs.push(manifest);
    }
    this.legs = uniqWith(this.legs, isEqual);
    this.wagonTable = this.generateWagonTableRows(this.wagons);
    this.changeDetector.markForCheck();
  }

  public getJPP(row: string): string {
    return parseFloat(row) + ' %';
  }

  // TODO: private
  public generateWagonTableHeaders() {
    const all: Promise<string>[] = [];
    ['#', ...this.wagonTableHeadersTranslationKeys].map(key => {
      all.push(this.translate.get(key).toPromise());
    });
    Promise.all(all).then(vals => {
      this.wagonTableHeaders = vals;
    });
  }

  /**
   * Generates Wagon data for both template and Excel export
   */
  public generateWagonTableRows(wagons: WagonRow[]) {
    // TODO: add type?
    const allrows = [];
    for (const [index, wagon] of wagons.entries()) {
      const getUniqueWagonCargoRows = (key) => {
        return this.getUniqueCargoRows(wagon, key);
      };
      const qmPipe = new QmDateFormatPipe(this.stateService);
      const ktrViimPurkAikaDate = getUniqueWagonCargoRows('KTR_VIIM_PURK_AIKA').map(value => {
        try {
          return value === undefined ? '' : qmPipe.transform(value, 'dd.MM.yyyy HH:mm');
        } catch (e) {
          console.error('Error parsing date value for KTR_VIIM_PURK_AIKA value: ', value);
          console.error(e);
        }
      });

      // value or empty. otherwise will include "undefined" in strings
      const voe = (val) => val ? val : '';

      const obj = {
        // actual columns in table
        'index': `${index + 1}`,
        'littera': `${voe(wagon.KAL_LITTERA)}`,
        'numero': `${voe(wagon.KAL_KALNRO)}`,
        'maakoodi': `${wagon.MAA_MAAKOODI}`,
        'ktiLahto': wagon.I.map(shipment => `${shipment.KTI_LAHTOASEMA}`),
        'ktiMaara': wagon.I.map(shipment => `${voe(shipment.KTI_MAARAASEMA)}`),
        'weight': this.calculateWagonWeight(wagon),
        'ktrTavaraNimi': getUniqueWagonCargoRows('KTR_TAVARANIMI'),
        'ktrRahtikirjaNro': getUniqueWagonCargoRows('KTI_RAHTIKIRJANUMERO'),
        'ktiLahettajat': wagon.I.map(shipment => shipment.KTI_LAHETTAJA_NIMI),
        'ktiVastaanottajat': wagon.I.map(shipment => shipment.KTI_VASTOTTAJA_NIMI),
        'ktrVaaraTunnus': getUniqueWagonCargoRows('KTR_VAARA_TUNNUS'),
        'ktrYkTunnus': getUniqueWagonCargoRows('KTR_YK_TUNNUS'),
        'ktrLipuke': getUniqueWagonCargoRows('KTR_LIPUKE'),
        'vakRowText': this.getVAKRowText(wagon),
        'ktrViimPurkAikaDate': ktrViimPurkAikaDate,
        // extra data
        'wagon': wagon,
        'shipments': wagon.I,
      };

      allrows.push(obj);
    }
    return allrows;
  }

  public exportToExcel() {
    const objs = this.generateJsonObjectsForExcel();
    const worksheet = XLSX.utils.json_to_sheet(objs, {header: this.wagonTableHeaders});
    console.log(worksheet);
    const workbook = {Sheets: {'data': worksheet}, SheetNames: ['data']};
    const excelBuffer: any = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
    console.log(excelBuffer);
    const data: Blob = new Blob([excelBuffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'});
    FileSaver.saveAs(data, this.train._id + '_export.xlsx');
  }

  // TODO: private
  public generateJsonObjectsForExcel() {
    const objArr = this.wagonTable.map(row => {
      const xlsObj: any = {};
      // FIXME: dangerous to do things like this, columns can get easily mixed up!
      // also no typings for the row object available so typos are likely
      xlsObj[this.wagonTableHeaders[0]] = row.index;
      xlsObj[this.wagonTableHeaders[1]] = row.littera;
      xlsObj[this.wagonTableHeaders[2]] = row.numero;
      xlsObj[this.wagonTableHeaders[3]] = row.maakoodi;
      xlsObj[this.wagonTableHeaders[4]] = row.ktiLahto;
      xlsObj[this.wagonTableHeaders[5]] = row.ktiMaara;
      xlsObj[this.wagonTableHeaders[6]] = row.weight;
      xlsObj[this.wagonTableHeaders[7]] = row.ktrTavaraNimi;
      xlsObj[this.wagonTableHeaders[8]] = row.ktrRahtikirjaNro.length === 1 ?
        // workaround to prevent SheetJS/Excel from detecting a single value as a number,
        // thus preventing the leading zero from showing
        row.ktrRahtikirjaNro[0] + ' ' : row.ktrRahtikirjaNro;
      xlsObj[this.wagonTableHeaders[9]] = row.ktiLahettajat;
      xlsObj[this.wagonTableHeaders[10]] = row.ktiVastaanottajat;
      xlsObj[this.wagonTableHeaders[11]] = row.ktrVaaraTunnus;
      xlsObj[this.wagonTableHeaders[12]] = row.ktrYkTunnus;
      xlsObj[this.wagonTableHeaders[13]] = row.ktrLipuke;
      xlsObj[this.wagonTableHeaders[14]] = row.vakRowText;
      xlsObj[this.wagonTableHeaders[15]] = row.ktrViimPurkAikaDate;

      this.wagonTableHeaders.forEach(key => {
        // just making sure, SheetJS probably takes care of this
        if (xlsObj[key] === undefined) {
          xlsObj[key] = '';
        }
        // automatically add linebreaks
        if (Array.isArray(xlsObj[key])) {
          xlsObj[key] = xlsObj[key].join('\n');
        }
      });
      return xlsObj;
    });
    return objArr;
  }

  public getShipmentInfoForWagon(wagon: WagonRow): ShipmentInfo[] {
    try {
      let shipmentInfo: any = wagon.I;
      if (!Array.isArray(shipmentInfo)) {
        if (!shipmentInfo) {
          shipmentInfo = [];
        } else {
          shipmentInfo = [shipmentInfo];
        }
      }
      return shipmentInfo;
    } catch (e) {
      console.warn(e);
      return [{} as any];
    }
  }

  public getCargoInfoForRow(shipment: ShipmentInfo[]): CargoInfo[] {
    try {
      let cargoInfo: CargoInfo[] = [];
      for (const shipmentInfo of shipment) {
        if (!shipmentInfo) {
          return [{} as any];
        }
        if (Array.isArray(shipmentInfo.J)) {
          cargoInfo = cargoInfo.concat(shipmentInfo.J);
        } else {
          cargoInfo.push(shipmentInfo.J);
        }
      }
      return cargoInfo;
    } catch (e) {
      console.warn(e);
      return [{} as any];
    }
  }

  public getVAKLink(id: string) {
    console.log(typeof id);
    console.log(id);
    if (!this.vrGroupService.getVAKLinkForId(parseInt(id))) {
      return 'https://vak.trafi.fi/vak-haku_2017/aine/' + id + '/';
    }
    return this.vrGroupService.getVAKLinkForId(parseInt(id));
  }

  public getUniqueCargoRows(wagon: WagonRow, field: string) {
    return uniqBy(this.getCargoInfoForRow(wagon.I), o => {
      return o[field];
    }).map(o => {
      return o[field];
    });
  }

  public calculateWagonWeight(wagon: WagonRow) {
    let weight = parseFloat(wagon.KAL_TAARA);
    this.getCargoInfoForRow(this.getShipmentInfoForWagon(wagon)).forEach(cargo => {
      weight += parseFloat(cargo.KTR_TAVARAN_TODPAINO) / 1000;
    });
    return round(weight, 2);
  }

  public getVAKRowText(wagon: WagonRow): string {
    try {
      let str = '';
      for (const row of uniqBy(this.getCargoInfoForRow(wagon.I), o => {
        return o.KTR_VAK_TEKNKUVAUS;
      })) {
        if (row.KTR_VAK_TEKNKUVAUS) {
          str += row.KTR_VAK_TEKNKUVAUS;
          // if (row.KTR_VAK_LISATIETO) {
          //   str += ' (' + row.KTR_VAK_LISATIETO + ')';
          // }
          str += '<br />';
        }
      }
      if (str.length > 0) {
        str = str.substring(0, str.length - 6);
      }
      return str;
    } catch (e) {
      console.warn(e);
      return '';
    }
  }

  public openHover(manifest: ShipmentInfo | WagonRow, hover: 'RECIPIENT' | 'SENDER' | 'WAGON' | 'SHIP', $event: any) {
    this.hoverType = hover;
    if (hover === 'RECIPIENT' || hover === 'SENDER') {
      this.selectedShipmentRow = manifest as ShipmentInfo;
    } else {
      this.selectedWagonRow = manifest as WagonRow;
    }
    this.detailsOverlay.show($event);

  }

  public hide(): void {
    this.selectedShipmentRow = null;
    this.selectedWagonRow = null;
    this.detailsOverlay.hide();
  }
}
